"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";
import { useAuth } from "./AuthContext";

const ComplaintsContext = createContext();

export const ComplaintsProvider = ({ children }) => {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { isAuthenticated, token } = useAuth();

  // ✅ Safe JSON parser
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // FETCH COMPLAINTS
  const fetchComplaints = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${BASE_URL}/complaints`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        setError("Failed to fetch complaints");
        return {
          success: false,
          message: "Failed to fetch complaints",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        setComplaints(data.data || []);
        return {
          success: true,
          data: data.data,
        };
      }

      setError(data.message || "Failed to fetch complaints");

      return {
        success: false,
        message: data.message || "Failed to fetch complaints",
      };
    } catch (err) {
      console.error("Error fetching complaints:", err);

      setError("Network error. Please try again.");

      return {
        success: false,
        message: "Network error. Please try again.",
      };
    } finally {
      setLoading(false);
    }
  };

  // CREATE COMPLAINT
  const createComplaint = async (formData) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${BASE_URL}/complaints`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to create complaint",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchComplaints();

        toast.success(data.message || "Complaint created successfully");

        return {
          success: true,
          tracking_id: data.tracking_id,
          message: data.message,
          data,
        };
      }

      setError(data.message);

      return {
        success: false,
        message: data.message || "Failed to create complaint",
      };
    } catch (err) {
      console.error("Error creating complaint:", err);

      setError("Network error. Please try again.");

      return {
        success: false,
        message: "Network error. Please try again.",
      };
    } finally {
      setLoading(false);
    }
  };

  // UPDATE COMPLAINT
  const updateComplaint = async (id, formData) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${BASE_URL}/complaints/${id}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to update complaint",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchComplaints();

        toast.success(data.message || "Complaint updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      setError(data.message || "Failed to update complaint");

      return {
        success: false,
        message: data.message || "Failed to update complaint",
      };
    } catch (err) {
      console.error("Error updating complaint:", err);

      setError("Network error. Please try again.");

      return {
        success: false,
        message: "Network error. Please try again.",
      };
    } finally {
      setLoading(false);
    }
  };

  // DELETE COMPLAINT
  const deleteComplaint = async (id, deleteAuditId) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${BASE_URL}/complaints/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          delete_audit_id: deleteAuditId,
        }),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to delete complaint",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchComplaints();

        toast.success(data.message || "Complaint deleted successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      setError(data.message || "Failed to delete complaint");

      return {
        success: false,
        message: data.message || "Failed to delete complaint",
      };
    } catch (err) {
      console.error("Error deleting complaint:", err);

      setError("Network error. Please try again.");

      return {
        success: false,
        message: "Network error. Please try again.",
      };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;

    fetchComplaints();
  }, [isAuthenticated, token]);

  return (
    <ComplaintsContext.Provider
      value={{
        complaints,
        loading,
        error,
        fetchComplaints,
        createComplaint,
        updateComplaint,
        deleteComplaint,
      }}
    >
      {children}
    </ComplaintsContext.Provider>
  );
};

export const useComplaints = () => useContext(ComplaintsContext);